import {
  type AdminCreateDropRequestInput,
  type AdminDropDto,
  type AdminUpdateDropRequest,
  DAILY_DROP_LIMITS,
  type DropEligibilityRuleInput,
  type DropKind,
  type DropParticipation,
} from '@pulsify/shared'

export type DropLinkKind = 'none' | 'event' | 'listing'

export interface DropFormState {
  kind: DropKind
  participation: DropParticipation
  title: string
  description: string
  prizeName: string
  prizeValueEur: string
  winnerCount: string
  minAge: '16' | '18'
  startsAt: string
  drawAt: string
  claimWindowDays: string
  terms: string
  linkKind: DropLinkKind
  linkedId: string
  rules: DropEligibilityRuleInput[]
  prizeContingent: string
  winsPerDay: string
  voucherValidDays: string
  voucherNote: string
}

export type DropFormProblem =
  | 'titleRequired'
  | 'prizeNameRequired'
  | 'termsRequired'
  | 'prizeValueFormat'
  | 'drawAfterStart'
  | 'contingentRequired'
  | 'contingentOnlyGrows'
  | 'winsPerDayRequired'
  | 'placeRequired'

const DAY_MS = 24 * 60 * 60 * 1000

/** An ISO instant as the browser's `datetime-local` value, in the admin's own time zone. */
function toLocalInputValue(iso: string): string {
  const d = new Date(iso)
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
}

export function defaultDropFormState(now: Date): DropFormState {
  return {
    kind: 'draw',
    participation: 'app',
    title: '',
    description: '',
    prizeName: '',
    prizeValueEur: '',
    winnerCount: '1',
    minAge: '18',
    startsAt: toLocalInputValue(now.toISOString()),
    drawAt: toLocalInputValue(new Date(now.getTime() + 7 * DAY_MS).toISOString()),
    claimWindowDays: '7',
    terms: '',
    linkKind: 'none',
    linkedId: '',
    rules: [],
    prizeContingent: '',
    winsPerDay: '',
    voucherValidDays: String(DAILY_DROP_LIMITS.voucherValidDaysDefault),
    voucherNote: '',
  }
}

export function dropFormStateFromDrop(drop: AdminDropDto): DropFormState {
  return {
    kind: drop.kind,
    participation: drop.participation,
    title: drop.title,
    description: drop.description,
    prizeName: drop.prizeName,
    prizeValueEur: drop.prizeValueEur && Number(drop.prizeValueEur) > 0 ? drop.prizeValueEur : '',
    winnerCount: String(drop.winnerCount),
    minAge: drop.minAge === 16 ? '16' : '18',
    startsAt: toLocalInputValue(drop.startsAt),
    drawAt: toLocalInputValue(drop.drawAt ?? drop.startsAt),
    claimWindowDays: String(drop.claimWindowDays),
    terms: drop.terms,
    linkKind: drop.linkedEventId ? 'event' : drop.linkedListingId ? 'listing' : 'none',
    linkedId: drop.linkedEventId ?? drop.linkedListingId ?? '',
    rules: drop.eligibilityRules
      .filter((rule) => rule.type !== 'invite_friends')
      .map((rule) => ({ type: rule.type, config: { ...rule.config } })),
    prizeContingent: drop.prizeContingent === null ? '' : String(drop.prizeContingent),
    winsPerDay: drop.winsPerDay === null ? '' : String(drop.winsPerDay),
    voucherValidDays: String(drop.voucherValidDays ?? DAILY_DROP_LIMITS.voucherValidDaysDefault),
    voucherNote: drop.voucherNote,
  }
}

function wholeNumberIn(value: string, max: number): number | null {
  const parsed = Number(value)
  return Number.isInteger(parsed) && parsed >= 1 && parsed <= max ? parsed : null
}

/** What keeps the form from being sent, per field; `live` is the running drop being edited. */
export function dropFormProblems(
  form: DropFormState,
  { live }: { live: AdminDropDto | null },
): Partial<Record<keyof DropFormState, DropFormProblem>> {
  const problems: Partial<Record<keyof DropFormState, DropFormProblem>> = {}
  if (!form.title.trim()) problems.title = 'titleRequired'
  if (!live && !form.prizeName.trim()) problems.prizeName = 'prizeNameRequired'
  if (!form.terms.trim()) problems.terms = 'termsRequired'
  const price = form.prizeValueEur.trim()
  if (price && !/^\d{1,8}(\.\d{1,2})?$/.test(price)) problems.prizeValueEur = 'prizeValueFormat'
  if (form.kind === 'draw') {
    if (!live && new Date(form.startsAt) >= new Date(form.drawAt)) {
      problems.drawAt = 'drawAfterStart'
    }
    return problems
  }
  const contingent = wholeNumberIn(form.prizeContingent, DAILY_DROP_LIMITS.contingentMax)
  if (contingent === null) problems.prizeContingent = 'contingentRequired'
  else if (live?.prizeContingent && contingent < live.prizeContingent) {
    problems.prizeContingent = 'contingentOnlyGrows'
  }
  if (wholeNumberIn(form.winsPerDay, DAILY_DROP_LIMITS.winsPerDayMax) === null) {
    problems.winsPerDay = 'winsPerDayRequired'
  }
  if (form.linkKind !== 'listing' || !form.linkedId) problems.linkedId = 'placeRequired'
  return problems
}

export function dropLinkage(form: DropFormState): {
  linkedEventId: string | null
  linkedListingId: string | null
} {
  return {
    linkedEventId: form.linkKind === 'event' && form.linkedId ? form.linkedId : null,
    linkedListingId: form.linkKind === 'listing' && form.linkedId ? form.linkedId : null,
  }
}

/** The whole drop as a draft sends it, created or edited. */
export function dropCreateRequest(
  form: DropFormState,
): AdminCreateDropRequestInput & AdminUpdateDropRequest {
  const base = {
    ...dropLinkage(form),
    kind: form.kind,
    participation: form.participation,
    title: form.title,
    description: form.description,
    prizeName: form.prizeName,
    prizeValueEur: form.prizeValueEur.trim() || undefined,
    minAge: form.minAge === '16' ? (16 as const) : (18 as const),
    startsAt: new Date(form.startsAt).toISOString(),
    terms: form.terms,
    rules: form.rules,
  }
  if (form.kind === 'daily') {
    return {
      ...base,
      prizeContingent: Number(form.prizeContingent),
      winsPerDay: Number(form.winsPerDay),
      voucherValidDays: Number(form.voucherValidDays) || DAILY_DROP_LIMITS.voucherValidDaysDefault,
      voucherNote: form.voucherNote,
    }
  }
  return {
    ...base,
    drawAt: new Date(form.drawAt).toISOString(),
    winnerCount: Number(form.winnerCount) || 1,
    claimWindowDays: Number(form.claimWindowDays) || 7,
  }
}

/** What a running drop may still change. */
export function liveDropUpdate(form: DropFormState): AdminUpdateDropRequest {
  return {
    title: form.title,
    description: form.description,
    terms: form.terms,
    ...dropLinkage(form),
    participation: form.participation,
    ...(form.kind === 'daily'
      ? { voucherNote: form.voucherNote, prizeContingent: Number(form.prizeContingent) }
      : {}),
  }
}
